import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Unit } from '../../models/unit';
import { debounceTime, distinctUntilChanged, map, take } from 'rxjs/operators';
import { QuillEditorComponent } from 'ngx-quill';
import * as Quill from 'quill';
import {
  UploadImageModalComponent,
  UploadImageModel,
  UploadImageType,
} from '../../../components/upload-image-modal/upload-image-modal.component';
import { ModalService } from '../../../lib/services/modal.service';
import { EditorText } from '../../../shared-module/components/mj-text-editor/mj-text-editor.component';

const emptyArray: any[] = [];

class ImageSnippet {
  pending: boolean = false;
  status: string = 'init';

  constructor(public src?: any, public file?: File) {}
}

@Component({
  selector: 'mj-unit-description',
  templateUrl: './unit-description.component.html',
  styleUrls: ['./unit-description.component.scss'],
})
export class UnitDescriptionComponent implements OnChanges, OnDestroy {
  @Input() public unit: Unit;
  // @Input() public formGroup: FormGroup;

  @Output() update: EventEmitter<Unit> = new EventEmitter<Unit>();
  descForm: FormGroup;
  formSub$: any;
  showEditor: boolean = true;

  public selectedFile;

  @ViewChild('editor', { static: false }) editor: QuillEditorComponent;
  @ViewChild('editor2', { static: false }) editor2: QuillEditorComponent;

  titleCounter = 0;
  descCounter = 0;

  placeholderTitleClass = 'inner placeholder-h3';
  placeholderDescClass = 'inner placeholder-p';

  constructor(
    private fb: FormBuilder,
    private ref: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private modalService: ModalService
  ) {
    this._initForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.unit && this.unit) {
      this.showEditor = false;
      this._fillForm(this.unit);
      this.selectedFile = null;
      this.showEditor = true;
    }
  }

  private _initForm() {
    this.descForm = this.fb.group(
      {
        description_title_rich: '',
        description_rich: '',
        description: '',
        description_image_media_id: null,
        description_image_url: '',
      },
      { updateOn: 'blur' }
    );

    this._onChanges();
  }

  private _fillForm(unit) {
    this.descForm.patchValue({
      description_title_rich: unit.description_title_rich,
      description_rich: unit.description_rich,
      description: unit.description,
      description_image_media_id: unit.description_image_media_id,
      description_image_url: unit.description_image_url,
    });
  }

  _onChanges() {
    this.formSub$ = this.descForm.valueChanges
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        map(desc => {
          if (this.descForm.dirty) {
            this.update.emit({ ...this.unit, ...desc });
            this.descForm.markAsPristine();
          }
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    if (this.formSub$) {
      this.formSub$.unsubscribe();
    }
  }

  get imageSrc() {
    return this.unitValue.description_image_url;
  }

  public updateDescriptionTitle($event: EditorText) {
    this.descForm.patchValue({
      description_title_rich: $event.rich,
    });
    this.descForm.markAsDirty();
  }

  public updateDescription($event: EditorText) {
    this.descForm.patchValue({
      description_rich: $event.rich,
      description: $event.plain,
    });
    this.descForm.markAsDirty();
  }

  public descEditorCreated(o: Quill) {
    this.descCounter = o.getText().length || 0;
    this.ref.detectChanges();
  }

  public titleEditorCreated(o: Quill) {
    this.titleCounter = o.getText().length || 0;
    o.format('header', 3);
    this.ref.detectChanges();
  }

  public onTitleContentChange(data: any) {
    this.titleCounter = data.text.length;
  }

  public onDescContentChange(data: any) {
    this.descCounter = data.text.length;
  }

  onUploadImage() {
    this.modalService
      .appendComponentToBody(
        UploadImageModalComponent,
        new UploadImageModel(UploadImageType.LISTING_IMAGE_TYPE, [3, 4])
      )
      .pipe(take(1))
      .subscribe(res => {
        if (res) {
          this.update.emit({
            ...this.unit,
            description_image_media_id: res.id,
            description_image_url: res.image,
          });
          this._snackBar.open('Image uploaded successfully', null, {
            duration: 3000,
          });
        }
      });
  }

  get titleModules() {
    const titleMods = {
      toolbar: {
        container: [
          ['bold', 'italic'],
          ['blockquote'],
          [{ header: [1, 2, 3, false] }],
          [{ font: emptyArray.slice() }],
          [{ align: emptyArray.slice() }],
        ],
      },
    };
    return titleMods;
  }

  get descModules() {
    const descMods = {
      toolbar: {
        container: [
          ['bold', 'italic'],
          ['blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link'],
          [{ header: [1, 2, 3, false] }],
          [{ font: emptyArray.slice() }],
          [{ align: emptyArray.slice() }],
        ],
      },
    };
    return descMods;
  }

  get descCounterValue() {
    return this.descCounter - 1;
  }

  get titleCounterValue() {
    return this.titleCounter - 1;
  }

  get unitValue() {
    return this.descForm.value;
  }
}
